import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { Box, Center, HStack, Text } from '@chakra-ui/react';
import { getCountryCallingCode } from 'libphonenumber-js/min';
import { isPlainObject, isEmpty, values } from 'lodash-es';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import { ALL_SUPPORTED_COUNTRIES_STRING } from '../../../../../constants/userSetting';
import authenticationService from '../../../../../service/authenticationService';
import pnotifyService from '../../../../../service/pnotifyService';
import { checkIsRegistrationExisted } from '../../../utils/checkRegisterStatus';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { getInputFieldProps } from '../../../utils/propsBuilder';
import extractServerErrorMessages from '../../../utils/extractServerErrorMessages';
import {
  PINCODE_COUNT_DOWN_TIME,
  PINCODE_LENGTH,
} from '../../../constants/pincode';
import {
  registerStatus,
  registrationErrorMessageMap,
} from '../../../constants/registerStatus';
import { CLASSNAME } from '../../../constants/style';
import {
  Input,
  MobilePhoneInput,
  TitleText,
  Button,
  ErrorBanner,
} from '../../../components';
import { useAppContext } from '../../../context/appContext';
import { useCountdownTimer } from '../../../hooks';

const VERIFY_CODE_FAILED = 'verifyCode failed';
const CHECK_REGISTER_STATUS_FAILED = 'checkRegisterStatus failed';
const VERIFY_ERRORS = [VERIFY_CODE_FAILED, CHECK_REGISTER_STATUS_FAILED];

const MobileForm = () => {
  const { t } = useTranslation();
  const {
    merchantData,
    userSetting,
    smsVerificationEnabled,
    redirectTo,
    grecaptcha,
    isRecaptchaEnabled,
    recaptchaSiteKey,
    currentUser,
  } = useAppContext();
  const {
    errors,
    control,
    formState,
    trigger,
    handleSubmit,
    setError,
  } = useFormContext();
  const supportedCountries =
    userSetting?.sms_verification?.supported_countries?.map((country) =>
      country.toLowerCase(),
    ) ?? [];
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const refSendCodeReCAPTCHA = useRef();
  const refUpdateUserReCAPTCHA = useRef();
  const {
    timeLeft,
    start: startTimer,
    isRunning: isTimerRunning,
  } = useCountdownTimer({
    defaultSeconds: PINCODE_COUNT_DOWN_TIME,
  });

  const { field: phoneCountryField } = useController({
    name: 'phone_country',
    control,
    defaultValue: merchantData.base_country_code,
  });
  const { field: mobilePhoneField } = useController({
    name: 'mobile_phone',
    control,
    defaultValue: '',
  });
  const { field: pincodeField } = useController({
    name: 'pincode',
    control,
    defaultValue: '',
  });

  const mobilePayload = useMemo(
    () => ({
      country_calling_code: getCountryCallingCode(phoneCountryField.value),
      mobile_phone: mobilePhoneField.value,
    }),
    [mobilePhoneField.value, phoneCountryField.value],
  );

  const checkRegisterStatus = async () => {
    try {
      const response = await authenticationService.checkRegisterStatus(
        mobilePayload,
      );
      const result = checkIsRegistrationExisted(response.data);

      if (result !== registerStatus.ALLOW_CREATE_MEMBER) {
        throw new Error(result);
      }
    } catch (error) {
      if (values(registerStatus).includes(error?.message)) {
        setError('mobile_phone', {
          type: 'api_response_error',
          message: t(registrationErrorMessageMap[error.message]),
        });
        throw CHECK_REGISTER_STATUS_FAILED;
      }

      // unexpected error
      throw error;
    }
  };
  const verifyCode = async () => {
    try {
      await authenticationService.verifyCode({
        ...mobilePayload,
        code: pincodeField.value,
      });
    } catch (error) {
      const errorMessages = error?.response?.data?.error_messages ?? [];

      if (errorMessages.length > 0) {
        setError('pincode', {
          type: 'custom',
          message: errorMessages.join(','),
        });
        throw VERIFY_CODE_FAILED;
      }

      // unexpected error
      throw error;
    }
  };
  const handleSubmitError = (error) => {
    if (isPlainObject(error?.response?.data)) {
      setErrorMessages(extractServerErrorMessages(error.response.data));
    } else if (!VERIFY_ERRORS.includes(error)) {
      // handle unexpected error
      console.error(error);
    }
  };

  const handleSendCode = async () => {
    const isValid = await trigger(['phone_country', 'mobile_phone']);
    if (!isValid) {
      return;
    }

    setErrorMessages([]);
    setIsSendingCode(true);

    try {
      await checkRegisterStatus();
      const token = await refSendCodeReCAPTCHA.current?.executeAsync();
      refSendCodeReCAPTCHA.current?.reset();
      startTimer();
      await authenticationService.sendCode({
        ...mobilePayload,
        is_forget_password: false,
        [RECAPTCHA_TOKEN_FIELD]: token,
      });
    } catch (error) {
      handleSubmitError(error);
    } finally {
      setIsSendingCode(false);
    }
  };

  const onSubmit = async () => {
    setErrorMessages([]);

    try {
      if (smsVerificationEnabled) {
        await verifyCode();
      } else {
        await checkRegisterStatus();

        const payload = {
          user: mobilePayload,
        };
        if (isRecaptchaEnabled) {
          payload[
            RECAPTCHA_TOKEN_FIELD
          ] = await refUpdateUserReCAPTCHA.current?.executeAsync();
          refUpdateUserReCAPTCHA.current?.reset();
        }

        await authenticationService.updateUser(currentUser._id, payload);
        pnotifyService.notify(t('Member info has been updated'), {});
      }

      window.location.href = redirectTo;
    } catch (error) {
      handleSubmitError(error);
    }
  };

  return (
    <>
      <Box mb="72px">
        <TitleText mb="16px">{t('Complete member info')}</TitleText>
        <Text
          color="#888"
          fontSize="14px"
          fontWeight={500}
          mb="32px"
          className={CLASSNAME.DESCRIPTION}
        >
          {t('Complete Phone Number Notice')}
        </Text>
        <ErrorBanner errorMessages={errorMessages} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <HStack gap="12px" mb="10px" alignItems="flex-start">
            <Box flex="1">
              <MobilePhoneInput
                formatOnInit={false}
                value={mobilePhoneField.value}
                onSelectFlag={(_number, selectedCountryData) => {
                  phoneCountryField.onChange(
                    selectedCountryData?.iso2?.toUpperCase(),
                  );
                  trigger('mobile_phone');
                }}
                onPhoneNumberBlur={(_isValid, newNumber) => {
                  mobilePhoneField.onBlur(
                    formatPhoneNumber({
                      mobilePhone: newNumber,
                      country: phoneCountryField.value.toUpperCase(),
                    }),
                  );
                }}
                onPhoneNumberChange={(_isValid, newNumber) => {
                  mobilePhoneField.onChange(newNumber.replace(/\+/, ''));
                }}
                label={t('Mobile Number')}
                defaultCountry={phoneCountryField.value}
                isInvalid={!isEmpty(errors?.mobile_phone?.message)}
                errorMessage={t(errors?.mobile_phone?.message)}
                onlyCountries={
                  supportedCountries.includes(ALL_SUPPORTED_COUNTRIES_STRING)
                    ? []
                    : supportedCountries
                }
              />
            </Box>
            {smsVerificationEnabled && (
              <>
                <Button
                  onClick={handleSendCode}
                  variant="outlineSendCode"
                  size="sm"
                  minWidth="80px"
                  mt="18px !important"
                  isLoading={isSendingCode}
                  isDisabled={
                    isTimerRunning ||
                    (!isEmpty(errors.mobile_phone) &&
                      errors.mobile_phone?.type !== 'api_response_error')
                  }
                >
                  {isTimerRunning
                    ? t('Seconds left', { seconds: timeLeft })
                    : t('Send verification code')}
                </Button>
                <ReCAPTCHA
                  ref={refSendCodeReCAPTCHA}
                  size="invisible"
                  grecaptcha={grecaptcha}
                  sitekey={recaptchaSiteKey}
                />
              </>
            )}
          </HStack>
          {smsVerificationEnabled && (
            <Box mb="10px">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={PINCODE_LENGTH}
                pattern={`\\d{${PINCODE_LENGTH}}`}
                placeholder={t('Enter verification code')}
                label={t('Verification code')}
                {...getInputFieldProps({
                  field: pincodeField,
                  error: errors?.pincode,
                  t,
                })}
              />
            </Box>
          )}
          <Button
            isFullWidth
            onClick={handleSubmit(onSubmit)}
            mt="32px"
            type="submit"
            isLoading={formState.isSubmitting}
          >
            {t('Complete')}
          </Button>
          {isRecaptchaEnabled && (
            <ReCAPTCHA
              ref={refUpdateUserReCAPTCHA}
              size="invisible"
              grecaptcha={grecaptcha}
              sitekey={recaptchaSiteKey}
            />
          )}
        </form>
        <Center mt="64px">
          <Button
            onClick={() => (window.location.href = redirectTo)}
            variant="link"
            size="sm"
          >
            {t('Skip')}
          </Button>
        </Center>
      </Box>
    </>
  );
};

export default MobileForm;
